import React from 'react';
import { Search, X } from 'lucide-react';

// Shared client-side filtering for the product audit log pages. The logs are
// already fetched (up to the page's limit), so filtering happens instantly
// in the browser without another round-trip.

export type AuditFilterState = {
  search: string;
  eventType: string;
  outcome: string;
};

export const emptyAuditFilters: AuditFilterState = {
  search: '',
  eventType: 'all',
  outcome: 'all',
};

type FilterableLog = {
  eventType?: string;
  outcome?: string;
};

export const filterAuditLogs = <T extends FilterableLog>(logs: T[], filters: AuditFilterState): T[] => {
  const q = filters.search.trim().toLowerCase();
  return logs.filter((log) => {
    if (filters.eventType !== 'all' && (log.eventType || '') !== filters.eventType) return false;
    if (filters.outcome !== 'all' && (log.outcome || '').toLowerCase() !== filters.outcome) return false;
    if (q) {
      // Search the whole record so UIDs, emails, reasons, and payment ids
      // inside nested detail objects all match.
      let haystack = '';
      try {
        haystack = JSON.stringify(log).toLowerCase();
      } catch {
        haystack = `${log.eventType || ''} ${log.outcome || ''}`.toLowerCase();
      }
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
};

const uniqueSorted = (values: Array<string | undefined>): string[] =>
  Array.from(new Set(values.filter((v): v is string => !!v))).sort();

type AuditLogFilterBarProps = {
  logs: FilterableLog[];
  filters: AuditFilterState;
  onChange: (next: AuditFilterState) => void;
  shownCount: number;
};

export const AuditLogFilterBar: React.FC<AuditLogFilterBarProps> = ({ logs, filters, onChange, shownCount }) => {
  const eventTypes = uniqueSorted(logs.map((l) => l.eventType));
  const outcomes = uniqueSorted(logs.map((l) => l.outcome?.toLowerCase()));
  const isFiltered = filters.search.trim() !== '' || filters.eventType !== 'all' || filters.outcome !== 'all';

  return (
    <div className="flex flex-col md:flex-row md:items-center gap-3 flex-1">
      <div className="relative flex-1 min-w-[220px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          aria-label="Search logs"
          placeholder="Search UID, email, event, reason..."
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/50 text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <label className="text-sm text-slate-700 dark:text-gray-300 inline-flex items-center gap-2">
        <span>Event</span>
        <select
          aria-label="Filter by event type"
          value={filters.eventType}
          onChange={(e) => onChange({ ...filters, eventType: e.target.value })}
          className="px-2 py-2 rounded-lg border border-slate-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/50 text-sm max-w-[260px]"
        >
          <option value="all">All events</option>
          {eventTypes.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </label>
      <label className="text-sm text-slate-700 dark:text-gray-300 inline-flex items-center gap-2">
        <span>Outcome</span>
        <select
          aria-label="Filter by outcome"
          value={filters.outcome}
          onChange={(e) => onChange({ ...filters, outcome: e.target.value })}
          className="px-2 py-2 rounded-lg border border-slate-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/50 text-sm"
        >
          <option value="all">All outcomes</option>
          {outcomes.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </label>
      {isFiltered && (
        <button
          onClick={() => onChange(emptyAuditFilters)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-800"
        >
          <X className="w-4 h-4" /> Clear
        </button>
      )}
      <span className="text-xs text-slate-500 dark:text-gray-400 whitespace-nowrap md:ml-auto">
        Showing {shownCount} of {logs.length}
      </span>
    </div>
  );
};
